import {
  Group,
  Button,
  TextInput,
  Text,
  Loader,
  Switch,
  Pill,
  PillsInput,
  Fieldset,
  AppShell,
} from "@mantine/core";
import { useAppDispatch, useAppSelector } from "../hooks";
import { RootState } from "../store";
import {
  displayErrorMessage,
  displayServerErrorMessage,
  handleFetchingError,
} from "../shared/ErrorMessage";
import {
  initialize,
  updateField,
  reset,
} from "../slices/form/userAttributesTab";
import { JSX, useEffect } from "react";
import {
  useSaveUserConfigAttributesMutation,
  SaveUserConfigAttributesRequest,
  useGetUserAttributesQuery,
} from "../slices/api/usersApi";
import { useAuth } from "../authentication/AuthProvider";
import { IsBlockNav, SetBlockNav } from "../App";
import { DisplayModeControl } from "../shared/components/DisplayModeControl";
import { AdminTabHeader } from "../shared/components/TabHeader";
import { EbTooltip } from "../shared/components/EbTooltip";
import { IconHelp } from "../shared/components/Icons";

const UserAttributesTab = () => {
  const authContext = useAuth();
  const { data, hasChanges } = useAppSelector(
    (state: RootState) => state.userAttributesTab,
  );
  const [saveUserAttributes, { isLoading: isSaving }] =
    useSaveUserConfigAttributesMutation();
  const {
    data: getUserAttributesResponse,
    isError: isErrorFetchingAttributes,
    error: attributesError,
    refetch: refetchAttributes,
  } = useGetUserAttributesQuery({});

  const dispatch = useAppDispatch();
  useEffect(() => {
    if (getUserAttributesResponse) {
      dispatch(initialize(getUserAttributesResponse));
    }
  }, [getUserAttributesResponse]);

  useEffect(() => {
    // During development useEffect is called twice by React.
    // We need to protect against that by returning if hasChanges didn't change
    if (IsBlockNav() === hasChanges) return;
    if (hasChanges) {
      SetBlockNav(true);
    } else {
      // Can happen if we support undo
      SetBlockNav(false);
    }
  }, [hasChanges]);

  const handleClick = async () => {
    if (data == null) {
      console.error("User attributes are null but should not be");
      return;
    }
    if (getUserAttributesResponse == null) {
      console.error("getUserAttributesResponse is null but should not be");
      return;
    }
    if (data.firstName == null) {
      displayErrorMessage("First name is required.");
      return;
    }

    const request: SaveUserConfigAttributesRequest = {
      firstName: data.firstName,
      lastName: data.lastName,
      displayMode: data.displayMode,
      isUnsubscribedAll: data.isUnsubscribedAll,
      isOptedOutTextChannel: data.isOptedOutTextChannel,
      canTestTextChannel: data.canTestTextChannel,
      unsubscribedHosts: data.unsubscribedHosts,
      smsNumber: data.smsNumber,
      updateTime: getUserAttributesResponse.updateTime,
    };

    try {
      const result = await saveUserAttributes(request).unwrap();
      authContext.setUserAttributes({ ...request, userId: result.userId });
    } catch (error) {
      displayServerErrorMessage(error);
    }
  };

  if (isErrorFetchingAttributes) {
    handleFetchingError(attributesError);
    return null;
  }

  if (!data || !getUserAttributesResponse) {
    return <Loader color="blue" />;
  }

  let pills: JSX.Element[] = [];
  pills = (data.unsubscribedHosts ?? []).map((email: string) => {
    return (
      <Pill
        key={email}
        withRemoveButton
        onRemove={() => {
          const newEmails = data.unsubscribedHosts.filter(
            (em: string) => em != email,
          );
          dispatch(
            updateField({
              field: "unsubscribedHosts",
              value: newEmails,
            }),
          );
        }}
      >
        {email}
      </Pill>
    );
  });

  return (
    <AppShell.Main>
      <AdminTabHeader label="Settings" />
      <TextInput
        required
        mt="md"
        label="First Name"
        style={{ width: "300px" }}
        value={data.firstName ?? ""}
        onChange={(v) => {
          dispatch(
            updateField({
              field: "firstName",
              value: v.currentTarget.value,
            }),
          );
        }}
      />
      <TextInput
        mt="md"
        label="Last Name"
        style={{ width: "300px" }}
        value={data.lastName ?? ""}
        onChange={(v) => {
          dispatch(
            updateField({
              field: "lastName",
              value: v.currentTarget.value,
            }),
          );
        }}
      />

      <TextInput
        mt="md"
        label="Mobile Number"
        description="Enter only if you wish to opt in to text messages. Hosts can elect to send texts in addition to emails. Format with country code, e.g. 12025550123"
        style={{ width: "300px" }}
        value={data.smsNumber ?? ""}
        onChange={(v) => {
          dispatch(
            updateField({
              field: "smsNumber",
              value: v.currentTarget.value,
            }),
          );
        }}
      />

      {data.isOptedOutTextChannel && (
        <Text>
          You have opted out of text messages for the above mobile number. The
          only way to opt back in is to send START to 18005551212.
        </Text>
      )}

      <DisplayModeControl
        label="Display Mode"
        mt="lg"
        value={data.displayMode}
        onChange={(value) =>
          dispatch(
            updateField({
              field: "displayMode",
              value: value,
            }),
          )
        }
      />
      <Group mt="md" gap="xs" align="center">
        <Switch
          label="Opt out of all host emails"
          checked={data.isUnsubscribedAll}
          onChange={(event) =>
            dispatch(
              updateField({
                field: "isUnsubscribedAll",
                value: event.currentTarget.checked,
              }),
            )
          }
        />
        <EbTooltip label="If set, you will not receive any emails sent by any host for any event">
          <span
            aria-label="Help"
            style={{ display: "inline-flex", cursor: "help" }}
          >
            <IconHelp />
          </span>
        </EbTooltip>
      </Group>
      <Fieldset
        legend="Host Information (refresh to see changes)"
        hidden={!getUserAttributesResponse?.canOwnVents}
        mt="md"
      >
        <Button
          mr="md"
          onClick={() => {
            refetchAttributes();
          }}
        >
          Refresh
        </Button>
        <Text mt="xs" mb="0" size="sm">
          Host Email Allotment Remaining:{" "}
          {getUserAttributesResponse?.emailAllotment ?? "none"}
        </Text>
        <Text mt="xs" mb="0" size="sm">
          Active Events Allowed: {getUserAttributesResponse?.ventLimit ?? 0}
        </Text>
        <Text mt="xs" mb="0" size="sm">
          Guests Per Event Allowed: {getUserAttributesResponse?.guestLimit ?? 0}
        </Text>
      </Fieldset>
      <PillsInput
        label="Unsubscribed hosts"
        description="You can unsubscribe for specific hosts by clicking the unsubscribe link on emails they send. Here you can only removed them."
      >
        <Pill.Group>{pills}</Pill.Group>
      </PillsInput>
      <div>
        <Button
          mt="xs"
          mr="md"
          onClick={() => {
            dispatch(reset());
          }}
          disabled={!hasChanges}
        >
          Discard Changes
        </Button>
        <Button
          mt="xs"
          mr="xl"
          onClick={handleClick}
          disabled={!hasChanges || isSaving}
          rightSection={isSaving ? <Loader size={14} /> : null}
        >
          Save Settings
        </Button>
      </div>
    </AppShell.Main>
  );
};

export default UserAttributesTab;
